import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Shared layout wrapper for /privacy and /terms. Renders the exact copy
 * provided by the user verbatim, do not edit the text.
 */
function LegalShell({ title, lastUpdated, children, testId }) {
  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid={testId}>
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="nb-border rounded-xl px-3 py-2 font-bold text-sm flex items-center gap-2 hover:nb-shadow">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <div className="font-display font-extrabold">ReviseAI</div>
          <div className="w-16" />
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-10 sm:py-14 prose-style">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-2">{title}</h1>
        <p className="text-sm text-zinc-500 font-bold mb-8">Last updated: {lastUpdated}</p>
        <div className="space-y-5 text-[15px] leading-relaxed text-zinc-800 dark:text-zinc-200">{children}</div>
      </article>
    </div>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl font-extrabold mt-10 mb-3 tracking-tight">{children}</h2>
);
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-1.5">{children}</ul>;

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" lastUpdated="16/10/2026" testId="privacy-page">
      <p>This policy explains what personal data ReviseAI collects, why, and what rights you have. ReviseAI is a subscription service that helps students revise for their GCSEs using AI.</p>

      <H2>Who we are</H2>
      <p>ReviseAI is operated by Truzolex Ltd, a company registered in England and Wales (company number 17126825), registered address 128 City Road, London, United Kingdom, EC1V 2NX. For anything about your data, contact us at <a href="mailto:isleofsagacity@gmail.com" className="underline">isleofsagacity@gmail.com</a>. Truzolex Ltd is the data controller for the personal data described here.</p>

      <H2>What we collect</H2>
      <UL>
        <li>Account details: your name, email address, and password (stored encrypted).</li>
        <li>Content you create or upload: revision notes, flashcards, revision books, podcasts, and chats with the study assistant.</li>
        <li>Payment data: handled entirely by Stripe, our payment processor. We never see or store your full card number. We keep a record of your subscription status and the last four digits of your card for your reference.</li>
        <li>Usage data: how you use the app, such as which features you open and your progress, so we can improve the service.</li>
        <li>Technical data: device type, browser, and IP address, collected automatically when you use the site.</li>
      </UL>

      <H2>Why we use it and our lawful basis</H2>
      <p>Under UK GDPR we rely on the following:</p>
      <UL>
        <li>To provide the service you signed up for (performance of a contract): running your account, saving your work, and giving you AI-generated revision material.</li>
        <li>To take payment and manage your subscription (performance of a contract).</li>
        <li>To improve and secure the service, and to understand how it is used (our legitimate interests).</li>
        <li>To send you service messages such as receipts and renewal reminders (performance of a contract), and marketing only where you have opted in (consent).</li>
      </UL>

      <H2>How long we keep it</H2>
      <p>We keep your account data for as long as your account is active, and for 12 months after you close it, unless the law requires us to keep it longer (for example, tax records). You can ask us to delete your data sooner.</p>

      <H2>Who we share it with</H2>
      <p>We do not sell your data. We share it only with providers who help us run the service, including Stripe for payments and our AI and hosting providers for generating and storing your revision content. These providers are bound to protect your data and use it only for the service they provide to us.</p>

      <H2>Your rights</H2>
      <p>Under UK GDPR you can ask us to:</p>
      <UL>
        <li>Give you a copy of the data we hold about you.</li>
        <li>Correct data that is wrong.</li>
        <li>Delete your data.</li>
        <li>Restrict or object to how we use it.</li>
        <li>Move your data to another service.</li>
      </UL>
      <p>To exercise any of these, email <a href="mailto:isleofsagacity@gmail.com" className="underline">isleofsagacity@gmail.com</a>. We will respond within one month. If you are unhappy with how we handle your data, you can complain to the Information Commissioner&apos;s Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noreferrer" className="underline">ico.org.uk</a>.</p>

      <H2>Children&apos;s data</H2>
      <p>ReviseAI is built for students aged 13 to 18, so we take extra care with younger users.</p>
      <UL>
        <li>If you are under 18, you should use ReviseAI with a parent or guardian&apos;s awareness, and a parent or guardian should set up or approve the subscription and payment.</li>
        <li>We collect only the data we need to provide revision material, and we do not use children&apos;s data for marketing.</li>
        <li>A parent or guardian can contact us at <a href="mailto:isleofsagacity@gmail.com" className="underline">isleofsagacity@gmail.com</a> to see, correct, or delete the data we hold about their child, or to close the account.</li>
        <li>We do not knowingly collect data from children under 13. If we learn that we have, we will delete it.</li>
      </UL>

      <H2>Cookies and analytics</H2>
      <p>We use essential cookies to keep you signed in and to run the service. We do not currently use third-party analytics or advertising cookies.</p>

      <H2>Changes to this policy</H2>
      <p>If we make significant changes, we will let you know by email or in the app before they take effect. The date at the top shows when this policy was last updated.</p>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell title="Terms of Service" lastUpdated="16/10/2026" testId="terms-page">
      <p>These terms set out the agreement between you and Truzolex Ltd for your use of ReviseAI. By creating an account or subscribing, you agree to them.</p>

      <H2>Who we are</H2>
      <p>ReviseAI is provided by Truzolex Ltd, registered in England and Wales (company number 17126825), registered address 128 City Road, London, United Kingdom, EC1V 2NX. Contact: <a href="mailto:isleofsagacity@gmail.com" className="underline">isleofsagacity@gmail.com</a>.</p>

      <H2>Using ReviseAI</H2>
      <p>ReviseAI gives you AI-generated revision material: revision books, flashcards, podcasts, and a study assistant, organised by subject. The content is a study aid. We work to keep it accurate and aligned to the GCSE curriculum, but we cannot guarantee it is free of errors, and it is not a substitute for your own study or your school&apos;s teaching.</p>
      <p>If you are under 18, a parent or guardian should set up and approve your subscription and payment.</p>

      <H2>Plans and pricing</H2>
      <p>We offer a free plan and paid subscriptions:</p>
      <UL>
        <li>Premium (1 child): £4.99 per month or £39 per year.</li>
        <li>Family (up to 3 children): £7.99 per month or £69 per year.</li>
      </UL>
      <p>Prices are shown in pounds sterling and include any applicable VAT. We may change our prices, but we will tell you before a change affects you, and any change applies from your next renewal.</p>

      <H2>Free trial</H2>
      <p>Paid plans include a 7-day free trial. You will not be charged during the trial. Unless you cancel before the trial ends, your subscription starts automatically and we charge the plan price. You can cancel at any time during the trial from your account settings.</p>

      <H2>Renewal and cancellation</H2>
      <p>Subscriptions renew automatically at the end of each billing period (monthly or yearly) using your payment method on file, until you cancel. You can cancel at any time from your account settings. When you cancel, you keep access until the end of the period you have already paid for, and you are not charged again.</p>

      <H2>Refunds</H2>
      <p>If you cancel during the 7-day free trial, you pay nothing. Outside the trial, payments are non-refundable except where required by law. Nothing in these terms affects your statutory rights under UK consumer law.</p>

      <H2>Acceptable use</H2>
      <p>You agree not to:</p>
      <UL>
        <li>Share your account or let others use your subscription beyond what your plan allows.</li>
        <li>Copy, resell, or redistribute ReviseAI content outside your own revision.</li>
        <li>Upload anything unlawful, or attempt to disrupt, reverse-engineer, or misuse the service.</li>
      </UL>
      <p>We may suspend or close accounts that break these rules.</p>

      <H2>Your content</H2>
      <p>You keep ownership of the notes and material you upload. You give us permission to process it so we can generate your revision material and run the service. We do not claim ownership of your content.</p>

      <H2>Liability</H2>
      <p>We provide ReviseAI with reasonable care and skill, but to the extent the law allows, we are not liable for exam results, indirect losses, or loss arising from reliance on AI-generated content. Nothing in these terms limits liability that cannot be limited by law, such as for death or personal injury caused by our negligence.</p>

      <H2>Governing law</H2>
      <p>These terms are governed by the laws of England and Wales, and disputes fall to the courts of England and Wales.</p>

      <H2>Changes to these terms</H2>
      <p>We may update these terms. If we make significant changes, we will tell you by email or in the app before they take effect.</p>
    </LegalShell>
  );
}
